import React, { useState } from "react";
import { Upload, AlertCircle, Trash2, ImageIcon, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Dialog, DialogContent } from "../ui/dialog";
import { toast } from "sonner";
import { rrApi, fmtDate } from "../../lib/rejectRemakeApi";
import { apiErrorMessage } from "../../lib/inventoryApi";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp"];

/**
 * EvidenceManager — upload input + gallery grid untuk 1 reject/remake record.
 * Props:
 *   - woId, rrId
 *   - evidence: array of evidence metadata { id, file_object_key, file_mime_type, ... }
 *   - canWrite: bool — role-gated upload button visibility
 *   - canDelete: bool — role-gated delete (Owner only)
 *   - onChange(): refetch parent detail
 */
export default function EvidenceManager({ woId, rrId, evidence = [], canWrite, canDelete, onChange }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { url, meta }

  const pick = (e) => {
    setErr(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!ALLOWED.includes(f.type)) {
      setErr("Jenis berkas ini tidak diizinkan. Hanya JPG/PNG/WEBP.");
      setFile(null); e.target.value = ""; return;
    }
    if (f.size > MAX_BYTES) {
      setErr(`Berkas ${(f.size / 1024 / 1024).toFixed(2)} MB melebihi batas 10 MB.`);
      setFile(null); e.target.value = ""; return;
    }
    if (f.size === 0) {
      setErr("Berkas kosong.");
      setFile(null); e.target.value = ""; return;
    }
    setFile(f);
  };

  const doUpload = async () => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      await rrApi.uploadEvidence(woId, rrId, file);
      toast.success("Bukti foto berhasil diunggah.");
      setFile(null);
      onChange?.();
    } catch (e) {
      setErr(apiErrorMessage(e, "Bukti foto gagal diunggah."));
    } finally {
      setBusy(false);
    }
  };

  const openLightbox = async (ev) => {
    // Refetch fresh signed URL untuk memastikan tidak expired.
    try {
      const r = await rrApi.signedUrl(woId, rrId, ev.id);
      setLightbox({ url: r.signed_url, meta: ev });
    } catch (e) {
      toast.error("Gagal membuka bukti foto.");
    }
  };

  const doDelete = async (evidenceId) => {
    try {
      await rrApi.deleteEvidence(woId, rrId, evidenceId);
      toast.success("Bukti foto dihapus.");
      onChange?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menghapus."));
    }
  };

  return (
    <div className="space-y-3" data-testid="rr-evidence-manager">
      {/* Gallery */}
      {evidence.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {evidence.map(ev => (
            <EvidenceTile
              key={ev.id}
              evidence={ev}
              woId={woId} rrId={rrId}
              onClick={() => openLightbox(ev)}
              canDelete={canDelete}
              onDelete={() => doDelete(ev.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic border border-dashed border-border rounded px-3 py-4 text-center">
          Belum ada bukti foto untuk laporan ini.
        </div>
      )}

      {/* Uploader (write roles only) */}
      {canWrite && (
        <div className="border border-dashed border-border rounded-md p-3 bg-muted/40">
          <div className="flex items-center gap-2 mb-2">
            <Upload size={14} className="text-muted-foreground" />
            <span className="text-xs font-medium">Unggah bukti foto (JPG/PNG/WEBP, maks 10 MB)</span>
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            onChange={pick}
            className="block w-full text-xs text-foreground file:mr-2 file:py-1 file:px-2 file:rounded file:border file:border-border file:bg-background file:text-xs file:font-medium file:cursor-pointer hover:file:bg-muted"
            data-testid="rr-evidence-input"
          />
          {file && (
            <div className="flex items-center justify-between gap-2 mt-2 text-xs text-muted-foreground bg-background border border-border rounded px-2 py-1.5">
              <span className="truncate">{file.name} · {(file.size / 1024).toFixed(1)} KB</span>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" onClick={doUpload} disabled={busy} data-testid="rr-evidence-submit">
                  {busy ? "Mengunggah…" : "Unggah"}
                </Button>
                <button type="button" onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground">
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
          {err && (
            <div className="flex items-start gap-2 mt-2 text-xs text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded px-2 py-1.5" data-testid="rr-evidence-error">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span className="break-all">{err}</span>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2">
          {lightbox && (
            <div className="space-y-2">
              <img
                src={lightbox.url}
                alt="Bukti foto"
                className="w-full max-h-[70vh] object-contain bg-muted rounded"
              />
              <div className="text-xs text-muted-foreground px-2">
                {(lightbox.meta.file_size_bytes / 1024).toFixed(1)} KB
                {" · "}Diunggah {fmtDate(lightbox.meta.uploaded_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Single evidence tile — click to open lightbox, delete button (owner). */
function EvidenceTile({ evidence, woId, rrId, onClick, canDelete, onDelete }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [tried, setTried] = useState(false);

  React.useEffect(() => {
    if (tried) return;
    setTried(true);
    rrApi.signedUrl(woId, rrId, evidence.id)
      .then(r => setThumbUrl(r.signed_url))
      .catch(() => setThumbUrl(null));
  }, [woId, rrId, evidence.id, tried]);

  return (
    <div className="relative group border border-border rounded overflow-hidden bg-muted aspect-square">
      <button
        type="button"
        onClick={onClick}
        className="block w-full h-full"
        data-testid={`rr-evidence-tile-${evidence.id}`}
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="Bukti foto" className="w-full h-full object-cover" />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <ImageIcon size={24} />
          </div>
        )}
      </button>
      {canDelete && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="absolute top-1 right-1 bg-background/90 hover:bg-background text-danger rounded p-1 opacity-0 group-hover:opacity-100 transition"
              data-testid={`rr-evidence-delete-${evidence.id}`}
              title="Hapus bukti foto (khusus Owner)"
            >
              <Trash2 size={14} />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus bukti foto?</AlertDialogTitle>
              <AlertDialogDescription>
                Bukti foto ini akan dihapus permanen dan tidak bisa dikembalikan. Hanya bisa dilakukan Owner.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete}>Hapus</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
